import React, { FC, useState, useEffect } from 'react';
import { Box, Button, Divider, Drawer, Grid, List, Text, useMediaQuery, useTheme } from '@maas-components/core';
import * as icons from '@maas-components/icons';
import chunk from 'lodash/chunk';
import useStyles from './styles';
import Favorites from './components/Favorites';
import MenuItem from './components/MenuItem';
import { Icon, Service } from '../types';

type MyAppsUrl = {
    url: string;
    external: boolean;
};

export type MenuProps = {
    services: Service[];
    open?: boolean;
    myAppsUrl?: MyAppsUrl | string;
    mktUrl?: string;
    onClose: (...args: unknown[]) => void;
    onClickMenuItem?: (item: Service) => void;
    enableColorDark?: boolean;
    RouterLink: React.ElementType;
};

const Menu: FC<MenuProps> = ({
    open,
    onClose,
    services,
    myAppsUrl,
    mktUrl,
    onClickMenuItem,
    enableColorDark,
    RouterLink,
}) => {
    const [favorites, setFavorites] = useState<string[]>([]);
    const [recent, setRecent] = useState<string[]>([]);
    const [favMobileSelectionEnabled, setFavMobileSelectionEnabled] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const mode = enableColorDark ? 'dark' : theme.palette.type;
    const classes = useStyles({ mode });
    const menuAnchor = isMobile ? 'left' : 'top';
    const gridWrap = isMobile ? 'wrap' : 'nowrap';
    const dividerOrientation = isMobile ? 'horizontal' : 'vertical';
    const drawerClasses = isMobile
        ? { paperAnchorLeft: classes.anchorPositionLeft }
        : { paperAnchorTop: classes.anchorPositionTop };

    const AppIcon = icons.Apps;

    useEffect(() => {
        const favoritesFromStorage = JSON.parse(localStorage.getItem('maas-menu-favorites') || '[]') as string[];
        const recentFromStorage = JSON.parse(localStorage.getItem('maas-recent-services') || '[]') as string[];

        setFavorites(favoritesFromStorage);
        setRecent(recentFromStorage);
    }, []);

    const myAppsButton = (
        <Box display="flex" justifyContent="center" alignItems="center">
            <AppIcon fontSize="small" className={classes.storeLinkIconColor} />
            <Text fontSize="medium" fontWeight="medium" className={classes.iconOtherApps}>
                Outros Apps
            </Text>
        </Box>
    );

    const getIcon = (iconName: Icon) => {
        const IconWrapper = icons[iconName];
        return <IconWrapper fontSize="small" className={classes.storeLinkIconColor} />;
    };

    const onFavoriteCheck = (item: string) => {
        const newFavorites = [...favorites, item].filter((value, index, self) => self.indexOf(value) === index);
        localStorage.setItem('maas-menu-favorites', JSON.stringify(newFavorites));
        setFavorites(newFavorites);
    };

    const onRecentCheck = (item: string) => {
        const newRecent = [...recent, item].filter((value, index, self) => self.indexOf(value) === index);
        localStorage.setItem('maas-recent-services', JSON.stringify(newRecent));
        setRecent(newRecent);
    };

    const onFavoriteRemove = (item: string) => {
        const newFavorites = favorites.filter((favorite) => favorite !== item);

        localStorage.setItem('maas-menu-favorites', JSON.stringify(newFavorites));
        setFavorites(newFavorites);
    };

    const renderMenu = () => {
        const formattedColumns = chunk(services, 6);
        const renderColumns = formattedColumns.map((column: Service[], key: number) => (
            <List className={classes.menuList} key={key}>
                {column.map((item: Service) => (
                    <MenuItem
                        mode={mode}
                        key={item.url + item.name}
                        item={item}
                        favorites={favorites}
                        favMobileSelectionEnabled={favMobileSelectionEnabled}
                        onFavoriteCheck={onFavoriteCheck}
                        onFavoriteRemove={onFavoriteRemove}
                        onRecentCheck={onRecentCheck}
                        onClose={onClose}
                        getIcon={getIcon}
                        onClickMenuItem={onClickMenuItem}
                        RouterLink={RouterLink}
                    />
                ))}
            </List>
        ));

        return renderColumns;
    };

    const renderFavorites = () =>
        favorites.map((item: string) => {
            const favorite = services.find((service) => service.url === item);

            if (favorite) {
                return (
                    <Favorites
                        mode={mode}
                        key={item}
                        favorite={favorite}
                        onFavoriteRemove={onFavoriteRemove}
                        onClose={onClose}
                        getIcon={getIcon}
                        RouterLink={RouterLink}
                    />
                );
            }

            return null;
        });

    const renderMyAppsLink = () => {
        if (!myAppsUrl) {
            return null;
        }

        if (typeof myAppsUrl === 'string') {
            return (
                <a href={myAppsUrl} className={classes.menuLink}>
                    {myAppsButton}
                </a>
            );
        }

        if (myAppsUrl.external) {
            return (
                <a href={myAppsUrl.url} className={classes.menuLink}>
                    {myAppsButton}
                </a>
            );
        }

        return (
            <RouterLink to={myAppsUrl.url} onClick={onClose} className={classes.menuLink}>
                {myAppsButton}
            </RouterLink>
        );
    };

    const renderMktLink = () => {
        if (!mktUrl) {
            return null;
        }

        return (
            <Box display="flex" justifyContent="center" alignItems="center">
                {getIcon('LaunchOutlined')}
                <a href={mktUrl} className={classes.storeLinkText} onClick={onClose}>
                    <Text fontSize="medium" fontWeight="medium">
                        Marketplace
                    </Text>
                </a>
            </Box>
        );
    };

    return (
        <Drawer
            classes={{ ...drawerClasses }}
            disablePortal
            style={{ zIndex: theme.zIndex.drawer + 1 }}
            className={classes.drawerContainer}
            anchor={menuAnchor}
            open={open}
            onClose={onClose}
            PaperProps={{ style: { position: 'relative' } }}
            BackdropProps={{ invisible: true }}
            elevation={isMobile ? 0 : 1}
        >
            <Grid className={classes.container} container wrap={gridWrap}>
                <Grid
                    className={classes.favoriteContainer}
                    direction="column"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sm={12}
                    md={3}
                    container
                    item
                >
                    <Grid item>
                        <Grid alignItems="center" container>
                            <Text className={classes.menuFavoritesTitle} fontSize="medium" fontWeight="medium">
                                Favoritos
                            </Text>
                            {isMobile && (
                                <Button
                                    variant="text"
                                    color="primary"
                                    size="medium"
                                    onClick={() => setFavMobileSelectionEnabled((prevState) => !prevState)}
                                >
                                    {favMobileSelectionEnabled ? 'Concluir' : 'Adicionar'}
                                </Button>
                            )}
                        </Grid>
                        <Grid alignItems="center" className={classes.favoriteInfo} container>
                            <Text fontSize="xsmall" color="textSecondary" className={classes.infoText}>
                                Marque seus aplicativos mais usados
                            </Text>
                        </Grid>
                        {renderFavorites()}
                    </Grid>
                    {!isMobile && <Grid item>{renderMktLink()}</Grid>}
                </Grid>
                <Grid item>
                    <Divider orientation={dividerOrientation} variant="fullWidth" />
                </Grid>
                <Grid className={classes.menuItemsContainer} sm={12} md={9} item container>
                    <Box width="100%" display="flex" justifyContent="space-between" mb={2}>
                        <Text className={classes.menuItemTitle} fontSize="medium" fontWeight="medium">
                            Menu
                        </Text>
                        {!isMobile && <Box mr={2}>{renderMyAppsLink()}</Box>}
                    </Box>
                    <Grid item className={classes.menuItemContent} wrap="wrap" container>
                        {renderMenu()}
                    </Grid>
                    {isMobile && (
                        <Grid
                            item
                            className={classes.menuLinksMobile}
                            direction="column"
                            alignItems="flex-start"
                            container
                        >
                            <div className={classes.menuLinksItemMobile}>{renderMyAppsLink()}</div>
                            <div className={classes.menuLinksItemMobile}>{renderMktLink()}</div>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Drawer>
    );
};

export default Menu;
